import type { Environment } from '@customerio/hydra-ui-api';
import { IdentifierKey } from './attribute-keys';

/** The per-identifier state map on the workspace environment (`{ cio_id, email, id, phone }`). */
export type IdentifiersConfig = NonNullable<Environment['identifiers']>;

export const IdentifierState = {
  Disabled: 0,
  Enabled: 1,
  EnabledImmutable: 2,
} as const;

export const IdentifierType = {
  Email: 'email',
  MultiIdentifier: 'multi',
} as const;

/** The environment fields that decide which attributes identify a person. */
export type WorkspaceIdentifierConfig = Pick<Environment, 'identifier_type' | 'identifiers'>;

function identifierEntries(identifiers: IdentifiersConfig | null | undefined): Array<[string, number]> {
  return identifiers == null ? [] : Object.entries(identifiers);
}

function identifierState(attribute: string, identifiers: IdentifiersConfig | null | undefined): number | undefined {
  return identifierEntries(identifiers).find(([key]) => key === attribute)?.[1];
}

/** Whether the workspace lets a person be identified by `attribute`. */
export function isAttributeEnabledIdentifier(
  attribute: string,
  identifiers: IdentifiersConfig | null | undefined,
): boolean {
  const state = identifierState(attribute, identifiers);
  return state != null && state !== IdentifierState.Disabled;
}

/** Whether `attribute` identifies a person and can never change once set. */
export function isAttributeImmutableIdentifier(
  attribute: string,
  identifiers: IdentifiersConfig | null | undefined,
): boolean {
  return identifierState(attribute, identifiers) === IdentifierState.EnabledImmutable;
}

/**
 * The identifiers a person can be added or updated by, in the order the
 * environment lists them. `cio_id` is system-assigned, so it is never one of
 * them.
 */
export function enabledIdentifiers(identifiers: IdentifiersConfig | null | undefined): string[] {
  return identifierEntries(identifiers)
    .filter(([key, state]) => key !== IdentifierKey.CioId && state !== IdentifierState.Disabled)
    .map(([key]) => key);
}

/** Every enabled identifier, `cio_id` included. */
export function allEnabledIdentifiers(identifiers: IdentifiersConfig | null | undefined): string[] {
  return identifierEntries(identifiers)
    .filter(([, state]) => state !== IdentifierState.Disabled)
    .map(([key]) => key);
}

export function isEmailIdentified(config: Pick<WorkspaceIdentifierConfig, 'identifier_type'>): boolean {
  return config.identifier_type === IdentifierType.Email;
}

export function isMultiIdentifier(config: Pick<WorkspaceIdentifierConfig, 'identifier_type'>): boolean {
  return config.identifier_type === IdentifierType.MultiIdentifier;
}

export function canIdentifyBy(identifier: IdentifierKey, identifiers: IdentifiersConfig | null | undefined): boolean {
  return enabledIdentifiers(identifiers).includes(identifier);
}
